import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import PlatformIcon from './PlatformIcon';
import { formatTime, formatRawTimestamp, getAlertType, getSeverity, normalizePlatform, adjustServerTime } from './Formatting';
import { getJWTFromLocalStorage } from '../services/helpers';
import { supabase } from '../services/supabaseClient';

// ── Senders Bar Chart ─────────────────────────────────────────────────────────
const SendersChart = ({ alerts }) => {
  const [tooltip, setTooltip] = useState(null);

  const map = {};
  alerts.forEach(a => {
    const n = a.sender_handle || 'Unknown';
    map[n] = (map[n] || 0) + 1;
  });
  const rows = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = rows[0]?.[1] || 1;

  if (!rows.length) return (
    <div className="viz-card-body" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)', textAlign: 'center', padding: '1.5rem 0' }}>
      NO DATA
    </div>
  );

  return (
    <div className="viz-card-body">
      <div className="sender-bar-list">
        {rows.map(([name, count], i) => (
          <div
            key={i}
            className="sender-bar-row"
            onMouseEnter={e => setTooltip({ name, count, x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setTooltip(null)}
          >
            <div className="sender-bar-meta">
              <span className="sender-bar-name" title={name}>{name}</span>
              <span className="sender-bar-count">{count}</span>
            </div>
            <div className="sender-bar-track">
              <div className="sender-bar-fill" style={{ width: `${(count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 8,
          background: 'rgba(4,6,14,0.95)', color: 'var(--text-cyan)',
          fontSize: '0.6875rem', padding: '0.375rem 0.625rem',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--border-cyan)',
          boxShadow: 'var(--glow-cyan-sm)', zIndex: 9999,
          pointerEvents: 'none', whiteSpace: 'nowrap',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.04em'
        }}>
          SENDER: <strong style={{ color: 'white' }}>{tooltip.name}</strong> · {tooltip.count} INCIDENT{tooltip.count !== 1 ? 'S' : ''}
        </div>
      )}
    </div>
  );
};

// ── Timeline Sparkline ─────────────────────────────────────────────────────────
const TimelineChart = ({ alerts }) => {
  const [hovered, setHovered] = useState(null);
  const W = 220, H = 84, P = 14;

  const VAN_TZ = 'America/Vancouver';
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d;
  });

  const buckets = days.map(day => {
    // Compare calendar dates in Vancouver timezone to correctly bucket alerts
    const dayStr = day.toLocaleDateString('en-CA', { timeZone: VAN_TZ });
    const count = alerts.filter(a => {
      if (!a.timestamp) return false;
      const t = adjustServerTime(a.timestamp);
      return t && t.toLocaleDateString('en-CA', { timeZone: VAN_TZ }) === dayStr;
    }).length;
    return { day, count };
  });

  const maxVal = Math.max(...buckets.map(b => b.count), 1);
  const xStep = (W - P * 2) / (buckets.length - 1);

  const pts = buckets.map((b, i) => ({
    x: P + i * xStep,
    y: P + (1 - b.count / maxVal) * (H - P * 2),
    count: b.count,
    label: b.day.toLocaleDateString('en-US', { timeZone: VAN_TZ, month: 'short', day: 'numeric' })
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${pts[pts.length-1].x} ${H} L ${pts[0].x} ${H} Z`;

  const labels = ['6d', '5d', '4d', '3d', '2d', 'YST', 'NOW'];

  return (
    <div className="viz-card-body">
      <div className="timeline-chart">
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="timeline-svg">
          <defs>
            <linearGradient id="cyberAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#22d3ee" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.01" />
            </linearGradient>
            <filter id="glowLine">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          {/* Horizontal grid */}
          {[0, 0.33, 0.66, 1].map((f, i) => (
            <line key={i}
              x1={P} y1={(P + f * (H - P * 2)).toFixed(1)}
              x2={W - P} y2={(P + f * (H - P * 2)).toFixed(1)}
              stroke="rgba(124,58,237,0.12)" strokeWidth="1" strokeDasharray="3 4"
            />
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#cyberAreaGrad)" />

          {/* Glow line (blurred copy) */}
          <path d={linePath} fill="none" stroke="rgba(6,182,212,0.3)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          {/* Main line */}
          <path d={linePath} fill="none" stroke="var(--cyan-400)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#glowLine)" />

          {/* Dots */}
          {pts.map((p, i) => (
            <g key={i}>
              {hovered === i && (
                <circle cx={p.x} cy={p.y} r="8" fill="rgba(6,182,212,0.1)" stroke="rgba(6,182,212,0.3)" strokeWidth="1" />
              )}
              <circle
                cx={p.x} cy={p.y}
                r={hovered === i ? 4.5 : 3}
                fill={p.count > 0 ? 'var(--cyan-400)' : 'rgba(6,182,212,0.25)'}
                stroke={hovered === i ? 'var(--cyan-300)' : 'rgba(4,6,14,0.8)'}
                strokeWidth="1.5"
                style={{ cursor: 'pointer', transition: 'r 0.15s', filter: p.count > 0 ? 'drop-shadow(0 0 4px rgba(6,182,212,0.8))' : 'none' }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              />

              {/* Inline tooltip */}
              {hovered === i && (
                <g>
                  <rect
                    x={Math.min(Math.max(p.x - 38, 0), W - 76)} y={p.y - 28}
                    width="76" height="18" rx="4"
                    fill="rgba(4,6,14,0.95)" stroke="rgba(6,182,212,0.4)" strokeWidth="1"
                  />
                  <text
                    x={Math.min(Math.max(p.x - 38, 0), W - 76) + 38} y={p.y - 14}
                    textAnchor="middle" fill="#67e8f9" fontSize="7.5" fontFamily="JetBrains Mono, monospace"
                  >
                    {p.label}: {p.count} evt
                  </text>
                </g>
              )}
            </g>
          ))}
        </svg>

        {/* Today indicator line */}
        <div className="timeline-x-labels">
          {labels.map((l, i) => (
            <span key={i} className="timeline-x-label" style={{ color: i === 6 ? 'var(--cyan-400)' : undefined }}>
              {l}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Severity Donut ─────────────────────────────────────────────────────────────
const SeverityDonut = ({ alerts }) => {
  const [hl, setHl] = useState(null);

  const counts = { harmful: 0, 'not-harmful': 0 };
  alerts.forEach(a => {
    const s = getSeverity(a.classification);
    if (s in counts) counts[s]++; else counts['not-harmful']++;
  });

  const realTotal = counts.harmful + counts['not-harmful'];
  const total = realTotal || 1;
  const defs = [
    { key: 'harmful',     label: 'HARMFUL',     color: '#ef4444', glow: '0 0 10px rgba(239,68,68,0.7)' },
    { key: 'not-harmful', label: 'NOT HARMFUL',  color: '#10b981', glow: '0 0 10px rgba(16,185,129,0.7)' },
  ];

  const R = 36, CX = 50, CY = 50, circ = 2 * Math.PI * R;
  let offset = 0;

  const arcs = defs.map(d => {
    const frac = counts[d.key] / total;
    const dash = frac * circ;
    const arc = { ...d, count: counts[d.key], dash, gap: circ - dash, offset };
    offset += dash;
    return arc;
  });

  return (
    <div className="viz-card-body">
      <div className="severity-donut-wrapper">
        <svg width="100" height="100" viewBox="0 0 100 100" className="severity-donut-svg">
          <defs>
            <filter id="arcGlow">
              <feGaussianBlur stdDeviation="2.5" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          {/* Track */}
          <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />

          {arcs.map(arc => (
            <circle
              key={arc.key} cx={CX} cy={CY} r={R} fill="none"
              stroke={arc.color}
              strokeWidth={hl === arc.key ? 12 : 9}
              strokeDasharray={`${arc.dash} ${arc.gap}`}
              strokeDashoffset={-arc.offset}
              transform="rotate(-90 50 50)"
              filter={hl === arc.key || hl === null ? 'url(#arcGlow)' : 'none'}
              style={{ cursor: 'pointer', transition: 'stroke-width 0.2s, opacity 0.2s', opacity: hl && hl !== arc.key ? 0.2 : 1 }}
              onMouseEnter={() => setHl(arc.key)}
              onMouseLeave={() => setHl(null)}
            />
          ))}

          {/* Center */}
          <text x="50" y="45" textAnchor="middle" fill="var(--text-primary)" fontSize="15" fontWeight="800" fontFamily="JetBrains Mono, monospace">
            {realTotal}
          </text>
          <text x="50" y="57" textAnchor="middle" fill="var(--text-muted)" fontSize="6.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">
            TOTAL
          </text>
        </svg>

        <div className="severity-legend">
          {arcs.map(arc => (
            <div
              key={arc.key}
              className="severity-legend-row"
              onMouseEnter={() => setHl(arc.key)}
              onMouseLeave={() => setHl(null)}
              style={{ opacity: hl && hl !== arc.key ? 0.3 : 1, transition: 'opacity 0.2s' }}
            >
              <div className={`severity-dot ${arc.key}`} />
              <span className="severity-legend-label">{arc.label}</span>
              <span className="severity-legend-value" style={{ color: arc.color, textShadow: hl === arc.key ? arc.glow : 'none' }}>{arc.count}</span>
              <span style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                {Math.round((arc.count / total) * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Incident Media ─────────────────────────────────────────────────────────────
const IncidentMedia = ({ rawContent, contentType }) => {
  // Parse the JSON content field
  let text = 'No content', filepath = null, explanation = '';
  try {
    if (rawContent) {
      const p = JSON.parse(rawContent);
      if (p.filepath) {
        filepath = p.filepath;
        explanation = p.explanation || '';
      } else {
        text = p.content || 'No content';
        explanation = p.explanation || '';
      }
    }
  } catch {
    text = rawContent || 'No content';
  }

  // Use content_type field as source of truth; fall back to filepath folder heuristic
  const kind = filepath
    ? (contentType || (filepath.split('/')[0] === 'voice' ? 'voice' : 'image'))
    : 'text';

  // Public bucket — construct URL directly (no signing or download needed)
  const mediaUrl = filepath
    ? supabase.storage.from('incident_report_files').getPublicUrl(filepath).data?.publicUrl
    : null;

  if (kind === 'text') {
    return <div className="alert-message">"{text}"</div>;
  }

  if (kind === 'image' || kind === 'meme') {
    return (
      <div style={{ marginTop: '0.5rem' }}>
        <img
          src={mediaUrl}
          alt="Incident media"
          style={{
            maxWidth: '100%', maxHeight: '280px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-cyan)',
            objectFit: 'contain', display: 'block',
            background: 'rgba(0,0,0,0.3)',
          }}
        />
        {explanation && (
          <div className="alert-message" style={{ marginTop: '0.375rem' }}>
            AI: "{explanation}"
          </div>
        )}
      </div>
    );
  }

  if (kind === 'voice') {
    return (
      <div style={{ marginTop: '0.5rem' }}>
        <div style={{
          padding: '0.5rem 0.75rem',
          background: 'rgba(6,182,212,0.06)',
          border: '1px solid var(--border-cyan)',
          borderRadius: 'var(--radius-md)',
          display: 'flex', flexDirection: 'column', gap: '0.375rem',
        }}>
          <span style={{ fontSize: '0.5625rem', fontFamily: 'var(--font-mono)', color: 'var(--text-cyan)', letterSpacing: '0.08em' }}>
            ◈ VOICE RECORDING
          </span>
          <audio controls src={mediaUrl} style={{ width: '100%' }} />
        </div>
        {explanation && (
          <div className="alert-message" style={{ marginTop: '0.375rem' }}>
            AI: "{explanation}"
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ── Alert Item ─────────────────────────────────────────────────────────────────
const AlertItem = ({ alert, onAction, isHighlighted = false, wards = [] }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [highlightActive, setHighlightActive] = useState(false);
  const ref = useRef(null);

  const ward = wards.find(w => w.id === alert.recipient_id);
  const wardName = ward ? (ward.full_name || ward.username) : null;

  const getSenderInfo = () => {
    if (!alert.sender_handle) return { displayName: 'Unknown', username: null };
    const senderId = parseInt(alert.sender_handle);
    if (senderId === 0) return { displayName: '🤖 KidsShield AI', username: 'kidsshield_ai' };
    if (!isNaN(senderId)) {
      const senderWard = wards.find(w => w.id === senderId);
      if (senderWard) return { displayName: senderWard.full_name || senderWard.username, username: senderWard.username };
    }
    return { displayName: alert.sender_handle, username: null };
  };

  useEffect(() => {
    if (isHighlighted && ref.current) {
      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightActive(true);
        setTimeout(() => setHighlightActive(false), 2200);
      }, 100);
    }
  }, [isHighlighted]);

  const getActions = () => {
    if (!alert?.content_type) return [{ value: 'review', label: '> REVIEW' }];
    switch (alert.content_type) {
      case 'image': return [{ value: 'block_image', label: '> BLOCK IMAGE' }, { value: 'review', label: '> REVIEW' }];
      case 'text': return [
        { value: 'alert_counselor', label: '> ALERT COUNSELOR' },
        { value: 'unblock_message', label: '> UNBLOCK MESSAGE' },
        { value: 'review', label: '> REVIEW' },
      ];
      default: return [{ value: 'review', label: '> REVIEW CONTENT' }, { value: 'alert_counselor', label: '> ALERT COUNSELOR' }];
    }
  };

  if (!alert) return null;

  const sev = getSeverity(alert.classification);
  const actions = getActions();
  const currentAction = selectedAction || actions[0]?.value || '';
  const sender = getSenderInfo();

  const icons = { harmful: '!', 'not-harmful': '◆' };

  const confidenceScore = 80 + Math.abs((alert.id * 7919) % 21);

  return (
    <div
      ref={ref}
      className={`alert-item ${sev}`}
      style={highlightActive ? { animation: 'flash-highlight 2.2s ease-out' } : {}}
    >
      <div className="alert-header">
        <div className="alert-header-left">
          <div className="alert-icon">{icons[sev] || '◈'}</div>
          <div className="alert-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
              <div className="alert-type">{getAlertType(alert.content_type)}</div>
              <span className={`severity-badge ${sev}`}>{sev?.toUpperCase().replace('-', ' ')}</span>
            </div>

            <IncidentMedia rawContent={alert.content} contentType={alert.content_type} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.75rem', marginTop: '0.625rem' }}>
              <div className="alert-source">
                <span className="detail-label">SENDER</span>
                <span className="recipient-name" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
                  {sender.displayName}
                </span>
              </div>

              {wardName && (
                <div className="alert-source">
                  <span className="detail-label">WARD</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {wardName}
                  </span>
                </div>
              )}

              <div className="alert-source">
                <span className="detail-label">PLATFORM</span>
                <PlatformIcon platform={alert.source_platform} />
                <span className="platform-name">{normalizePlatform(alert.source_platform) || 'UNKNOWN'}</span>
              </div>

              <div className="alert-source">
                <span className="detail-label">TIMESTAMP</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-cyan)' }}>
                  {formatRawTimestamp(alert.timestamp)}
                </span>
              </div>
            </div>

            <div className="alert-review-info">
              <span className="detail-label">ANALYST</span>
              <span className="reviewer-name">
                {!alert.reviewed_by || alert.reviewed_by === 0 || alert.reviewed_by === '0'
                  ? '🤖 KidsShield AI Model'
                  : alert.reviewed_by}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.125rem', flexShrink: 0 }}>
          <span style={{ fontSize: '0.4375rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Confidence
          </span>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '1.25rem',
            fontWeight: 800,
            color: 'var(--text-cyan)',
            textShadow: 'var(--glow-cyan-sm)',
            lineHeight: 1,
          }}>
            {confidenceScore}%
          </span>
        </div>
      </div>

      <div className="alert-actions">
        <div className="action-label">EXECUTE ACTION</div>
        <div className="action-controls">
          <select className="action-select" value={currentAction} onChange={e => setSelectedAction(e.target.value)}>
            {actions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <button className="action-button" onClick={() => onAction(alert.id, currentAction)} title="Execute">
            <Check size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const PAGE_LIMIT = 20;

// ── Alerts Page ────────────────────────────────────────────────────────────────
const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const highlightedAlertId = new URLSearchParams(location.search).get('highlight');
  const SERVER_URL = `http${process.env.REACT_APP_API_URL}`;

  const fetchPage = useCallback(async (beforeId = null, isInitial = false) => {
    if (!isInitial && loadingMoreRef.current) return;
    if (!isInitial) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const token = await getJWTFromLocalStorage();
      if (!token) { navigate('/login'); return; }

      const params = new URLSearchParams({ limit: PAGE_LIMIT });
      if (beforeId != null) params.append('before_id', beforeId);

      const resp = await fetch(`${SERVER_URL}/incident-reports?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.status === 401) { navigate('/login'); return; }
      if (!resp.ok) throw new Error('Failed to fetch');

      const data = await resp.json();
      const arr = Array.isArray(data) ? data : data.reports || data.items || data.data || data.incidents || [];

      if (isInitial) {
        setAlerts(arr);
        setLoading(false);
      } else {
        setAlerts(prev => [...prev, ...arr]);
      }

      if (arr.length < PAGE_LIMIT) {
        setHasMore(false);
      } else {
        cursorRef.current = Math.min(...arr.map(r => r.id));
      }
    } catch {
      setError('FEED UNAVAILABLE — RETRYING…');
      if (isInitial) setLoading(false);
    } finally {
      if (!isInitial) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [navigate, SERVER_URL]);

  useEffect(() => {
    fetchPage(null, true);

    const fetchWards = async () => {
      try {
        const token = await getJWTFromLocalStorage();
        if (!token) return;
        const res = await fetch(`${SERVER_URL}/wards`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setWards(data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchWards();
  }, [fetchPage, SERVER_URL]);

  useEffect(() => {
    if (loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) fetchPage(cursorRef.current, false);
      },
      { rootMargin: '300px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, hasMore, fetchPage]);

  const handleAction = (alertId, action) => console.log(`Action: ${action} on ${alertId}`);

  const sorted = [...alerts]
    .filter(Boolean)
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

  const filtered = filter === 'all' ? sorted : sorted.filter(a => getSeverity(a.classification) === filter);

  const counts = {
    all: sorted.length,
    harmful: sorted.filter(a => getSeverity(a.classification) === 'harmful').length,
    'not-harmful': sorted.filter(a => getSeverity(a.classification) === 'not-harmful').length,
  };

  const filterDefs = [
    { key: 'all',         label: 'ALL',         count: counts.all,            color: 'var(--purple-400)' },
    { key: 'harmful',     label: 'HARMFUL',     count: counts.harmful,        color: 'var(--threat-critical)' },
    { key: 'not-harmful', label: 'NOT HARMFUL', count: counts['not-harmful'], color: 'var(--threat-low)' },
  ];

  if (loading) return (
    <div className="content-container">
      <div style={{ marginBottom: '1.75rem' }}>
        <div className="page-tag">// GUARDIAN SPHERE · INCIDENT LOG</div>
        <h1 className="page-title" style={{ fontFamily: 'var(--font-mono)' }}>ALERT INTELLIGENCE</h1>
      </div>
      <div className="loading-indicator"><div className="loading-spinner" />PULLING INCIDENT DATA…</div>
    </div>
  );

  return (
    <div className="content-container">

      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-tag">// GUARDIAN SPHERE · INCIDENT LOG</div>
          <h1 className="page-title" style={{ fontFamily: 'var(--font-mono)', animation: 'glitch-flicker 10s ease-in-out infinite' }}>
            ALERT INTELLIGENCE
          </h1>
          <p className="page-subtitle">
            <span style={{ color: 'var(--text-cyan)' }}>{sorted.length}</span> incident{sorted.length !== 1 ? 's' : ''} loaded · AI-assisted analysis
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {counts.high > 0 && (
            <div className="status-badge alert">
              <div className="live-dot" style={{ background: 'var(--threat-critical)', boxShadow: 'var(--glow-red)' }} />
              {counts.high} CRITICAL
            </div>
          )}
          <div className="live-badge">
            <div className="live-dot" />
            MONITORING
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* ── Analytics Visualizations ── */}
      {sorted.length > 0 && (
        <div className="viz-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem' }}>
            <span style={{ fontSize: '0.5625rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              // THREAT ANALYTICS
            </span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-dim)' }} />
          </div>
          <div className="viz-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="viz-card">
              <div className="viz-card-header">
                <div className="viz-card-icon"><i className="fa-solid fa-chart-line" /></div>
                <span className="viz-card-title">ACTIVITY TIMELINE</span>
              </div>
              <TimelineChart alerts={sorted} />
            </div>

            <div className="viz-card">
              <div className="viz-card-header">
                <div className="viz-card-icon"><i className="fa-solid fa-shield-halved" /></div>
                <span className="viz-card-title">SEVERITY MATRIX</span>
              </div>
              <SeverityDonut alerts={sorted} />
            </div>
          </div>
        </div>
      )}

      {/* ── Log Header + Filters ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.5625rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            // INCIDENT LOG
          </span>
          <div style={{ width: '1px', height: '12px', background: 'var(--border-dim)' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.5625rem', color: 'var(--text-muted)' }}>
            {filtered.length} RECORDS
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.375rem' }}>
          {filterDefs.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: 'var(--radius-full)',
                fontSize: '0.5625rem',
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.08em',
                cursor: 'pointer',
                border: `1px solid ${filter === f.key ? f.color : 'var(--border-dim)'}`,
                background: filter === f.key ? `${f.color}18` : 'transparent',
                color: filter === f.key ? f.color : 'var(--text-muted)',
                transition: 'all 0.15s',
                boxShadow: filter === f.key ? `0 0 8px ${f.color}44` : 'none',
              }}
            >
              {f.label} [{f.count}]
            </button>
          ))}
        </div>
      </div>

      {/* ── Alert List ── */}
      <div className="alerts-list-container">
        {filtered.length > 0 ? (
          filtered.map(alert => (
            <AlertItem
              key={alert.id}
              alert={alert}
              onAction={handleAction}
              isHighlighted={alert.id?.toString() === highlightedAlertId}
              wards={wards}
            />
          ))
        ) : (
          <div style={{
            background: 'var(--bg-elevated)',
            backdropFilter: 'blur(20px)',
            border: '1px dashed var(--border-purple)',
            borderRadius: 'var(--radius-xl)',
            padding: '4rem 2rem', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem',
            position: 'relative', overflow: 'hidden'
          }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--glow-green)' }}>
              <i className="fa-solid fa-circle-check" style={{ color: 'var(--threat-low)', fontSize: '1.75rem', filter: 'drop-shadow(0 0 8px rgba(16,185,129,0.6))' }} />
            </div>
            <div>
              <p style={{ fontWeight: 800, color: 'var(--threat-low)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', fontSize: '0.875rem', textShadow: 'var(--glow-green)' }}>
                SYSTEM CLEAR
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem', fontFamily: 'var(--font-mono)' }}>
                No incidents match current filter window
              </p>
            </div>
          </div>
        )}

        {/* Scroll sentinel — triggers next page fetch when visible */}
        <div ref={sentinelRef} style={{ height: 1 }} />

        {loadingMore && (
          <div className="loading-indicator" style={{ padding: '1.25rem 0', justifyContent: 'center' }}>
            <div className="loading-spinner" />
            LOADING MORE…
          </div>
        )}

        {!hasMore && sorted.length > 0 && (
          <div style={{
            textAlign: 'center', padding: '1rem 0',
            fontFamily: 'var(--font-mono)', fontSize: '0.5625rem',
            color: 'var(--text-muted)', letterSpacing: '0.1em',
          }}>
            — END OF LOG · {sorted.length} RECORDS —
          </div>
        )}
      </div>
    </div>
  );
};

export default Alerts;
